import apiClient from './client';

export type CaseType = 'deposit_review' | 'comparison' | 'deficiency';

export interface Case {
  id: string;
  case_number: string;
  status: string;
  current_stage?: string;
  review_in_progress?: boolean;
  needs_manual_review: boolean;
  modified_since_review?: boolean;
  manual_review_fields: string[];
  created_by: string;
  created_at: string;
  updated_at: string;
  // DataWash rebuild: last activity timestamp for sidebar sorting.
  last_activity_at?: string;
  // DataWash rebuild: unread badge for the sidebar.
  unread?: boolean;
  // DataWash rebuild: case type (deposit_review | comparison | deficiency).
  type?: CaseType;
  // DataWash rebuild: entity name for the sidebar row.
  entity_name?: string;
  // DataWash rebuild: human-friendly title.
  title?: string;
  assigned_analyst_id?: string | null;
  workflow_id?: string | null;
  current_step_order?: number;
  // Extended fields populated from facts or backend
  issuer_name?: string;
  ticker?: string;
  cusip?: string;
  cik?: string;
  depositor_name?: string;
  depositor_type?: string;
  account_ref?: string;
  deposit_date?: string;
  share_quantity?: number;
  notes?: string;
  tier?: string;
  // Archive / assignment fields (DataWash case management).
  is_archived?: boolean;
}

export interface CaseFact {
  field_name: string;
  field_value: string | null;
  provenance: Record<string, unknown>;
  is_verifiable: boolean;
  source_type: 'document' | 'public_lookup' | 'manual';
}

// Intake discrepancy row (read-join surfaced on the case detail response
// for the overview "Rejected" badge on gated identifiers, operator
// 2026-11-04). `conflicting_value` is the form-layout-identified value
// the identifier gate rejected (e.g. a check-digit-invalid CUSIP).
export interface IntakeDiscrepancy {
  field_name: string;
  master_value: string | null;
  conflicting_value: string | null;
  created_at: string | null;
}

export interface CaseDetail {
  case: Case;
  facts: CaseFact[];
  intake_discrepancies?: IntakeDiscrepancy[];
}

export interface CaseListParams {
  status?: string;
  needs_manual_review?: boolean;
  page?: number;
  page_size?: number;
  mine_only?: boolean;
  // DataWash rebuild: filter by case type + free-text search.
  type?: CaseType;
  search?: string;
  // DataWash rebuild: include archived cases in the list.
  archived?: boolean;
  // DataWash rebuild: filter for cases with completed deficiency jobs.
  has_deficiencies?: boolean;
}

export interface CaseListResponse {
  items: Case[];
  total: number;
  page: number;
  page_size: number;
}

export interface DocumentItem {
  id: string;
  case_id: string;
  original_filename: string;
  system_filename: string;
  mime_type: string;
  file_size_bytes: number;
  document_type: string;
  classification: string;
  doc_category?: string | null;
  processing_status: 'OK' | 'ERRORED' | 'NOT_VERIFIABLE' | 'pending' | 'ingesting' | 'cancelled' | 'ok' | 'errored' | 'not_verifiable';
  extraction_metadata: Record<string, unknown>;
  created_at: string;
  extracted_text?: string | null;
  is_purged?: boolean;
  purged_at?: string | null;
  is_encrypted?: boolean;
  has_pii_stripped_text?: boolean;
  // auto-replace detection (docs-diff-viewer)
  replacement_of_id?: string | null;
  auto_compare_job_id?: string | null;
  version_label?: string | null;
  // content-based replacement detection (replacement-detect task)
  replacement_detection?: 'auto' | 'manual' | null;
  replacement_similarity?: number | null;
  replaces_document_filename?: string | null;
  processing_error?: string | null;
}

export interface IntakeResult {
  case_id: string;
  documents_queued: number;
  auto_fill_result: {
    identified_fields: string[];
    missing_fields: string[];
    needs_manual_review: boolean;
  };
}

export interface CreateCaseData {
  notes?: string;
  issuer_name?: string;
  issuer_symbol?: string;
  cusip?: string;
  cik?: string;
  depositor_name?: string;
  depositor_type?: string;
  account_ref?: string;
  deposit_date?: string;
  share_quantity?: number;
  // DataWash rebuild: case type + title + entity name.
  type?: CaseType;
  title?: string;
  entity_name?: string;
}

export async function listCases(params?: CaseListParams): Promise<CaseListResponse> {
  const { data } = await apiClient.get<CaseListResponse>('/cases', { params });
  return data;
}

export async function getCase(id: string): Promise<CaseDetail> {
  const { data } = await apiClient.get<CaseDetail>(`/cases/${id}`);
  return data;
}

export async function createCase(caseData: CreateCaseData): Promise<Case> {
  const { data } = await apiClient.post<Case>('/cases', caseData);
  return data;
}

export async function updateCase(
  id: string,
  updates: { facts?: CaseFact[]; notes?: string } & Partial<CreateCaseData> & {
    is_archived?: boolean;
    assigned_analyst_id?: string | null;
  }
): Promise<Case> {
  const { data } = await apiClient.patch<Case>(`/cases/${id}`, updates);
  return data;
}

// Re-pull public data + auto-launch a review run (single "Update &
// Re-review" flow). Returns the new review run id.
export async function reReviewCase(id: string): Promise<{
  run_id: string;
  status: string;
  case_id: string;
  public_data_run_id: string | null;
}> {
  const { data } = await apiClient.post(`/cases/${id}/re-review`);
  return data;
}

export async function deleteCase(id: string): Promise<void> {
  await apiClient.delete(`/cases/${id}`);
}

export interface PackageDropResponse {
  case_id: string;
  case_number: string;
  status: string;
  needs_manual_review: boolean;
  missing_fields: string[];
  extracted_facts: Array<{
    field_name: string;
    field_value: string | null;
    source: string | null;
    path: string | null;
    status: string;
  }>;
  documents: Array<{
    document_id: string;
    original_filename: string;
    processing_status: string;
    processing_error: string | null;
    classification: string | null;
    extracted_facts: Array<{
      field_name: string;
      field_value: string | null;
      source: string | null;
      status: string;
    }>;
  }>;
  issuer_name: string | null;
  issuer_symbol: string | null;
  cusip: string | null;
  cik: string | null;
  depositor_name: string | null;
  depositor_type: string | null;
  account_ref: string | null;
  deposit_date: string | null;
  share_quantity: number | null;
  notes: string | null;
}

export interface PackageDropConfirmResponse {
  case_id: string;
  case_number: string;
  status: string;
  review_triggered: boolean;
  review_run_id: string | null;
  message: string;
}

export async function packageDrop(
  files: File[],
  skipAi: boolean = false,
  ingestStrippingOverride: string | null = null,
): Promise<PackageDropResponse> {
  const formData = new FormData();
  if (skipAi) formData.append('skip_ai', 'true');
  if (ingestStrippingOverride) formData.append('ingest_stripping_override', ingestStrippingOverride);
  files.forEach((file) => formData.append('files', file));
  const { data } = await apiClient.post<PackageDropResponse>('/intake/package-drop', formData);
  return data;
}

export async function confirmPackageDrop(
  caseId: string,
  updates: Record<string, unknown>,
  runReview: boolean
): Promise<PackageDropConfirmResponse> {
  const editedFields = Object.entries(updates).map(([fieldName, fieldValue]) => ({
    field_name: fieldName,
    field_value: String(fieldValue),
  }));
  const { data } = await apiClient.post<PackageDropConfirmResponse>('/intake/package-drop/confirm', {
    case_id: caseId,
    edited_fields: editedFields,
    run_review: runReview,
  });
  return data;
}

export async function listDocuments(
  caseId: string
): Promise<{ items: DocumentItem[] }> {
  const { data } = await apiClient.get(`/cases/${caseId}/documents`);
  return data;
}

export async function uploadDocuments(
  caseId: string,
  files: File[],
  documentType?: string,
  classification?: string
): Promise<{ items: DocumentItem[] }> {
  // The backend POST /cases/{id}/documents accepts a single `file`
  // (UploadFile) plus `doc_category`/`classification` form fields, so upload
  // one file per request and aggregate the results. (Field name is `file`,
  // singular; `doc_category` is the backend's name for documentType.)
  const items: DocumentItem[] = [];
  for (const file of files) {
    const formData = new FormData();
    formData.append('file', file);
    if (documentType) formData.append('doc_category', documentType);
    if (classification) formData.append('classification', classification);
    const { data } = await apiClient.post<{ items: DocumentItem[] }>(
      `/cases/${caseId}/documents`,
      formData
    );
    if (data?.items) items.push(...data.items);
  }
  return { items };
}

export async function getDocument(
  _caseId: string,
  documentId: string
): Promise<DocumentItem & { extracted_text?: string | null; classification?: string | null; doc_category?: string | null; processing_error?: string | null }> {
  const { data } = await apiClient.get(`/documents/${documentId}`);
  return data;
}

export interface DocumentText {
  document_id: string;
  filename: string;
  text: string;
  page_count: number;
  processing_status: string;
}

/** Fetch extracted text with --- Page N --- markers (docs-diff-viewer). */
export async function getDocumentText(
  documentId: string
): Promise<DocumentText> {
  const { data } = await apiClient.get<DocumentText>(
    `/documents/${documentId}/text`
  );
  return data;
}

export async function runReview(caseId: string): Promise<unknown> {
  const { data } = await apiClient.post(`/cases/${caseId}/reviews`);
  return data;
}

// Purge a document's original file bytes (keeps the metadata row for the
// audit trail). Compliance/operator only on the backend.
export async function purgeDocument(documentId: string): Promise<void> {
  await apiClient.delete(`/documents/${documentId}`);
}

// --- replacement detection (replacement-detect task) ---
// Manually mark a document as a replacement for another document in the
// same case. Sets replacement_detection='manual' on the backend.
export async function assignReplacement(
  documentId: string,
  replacesDocumentId: string
): Promise<DocumentItem> {
  const { data } = await apiClient.post<DocumentItem>(
    `/documents/${documentId}/assign-replacement`,
    { replaces_document_id: replacesDocumentId }
  );
  return data;
}

// Detach a document from its replacement relationship (clears the link).
export async function detachReplacement(
  documentId: string
): Promise<DocumentItem> {
  const { data } = await apiClient.delete<DocumentItem>(
    `/documents/${documentId}/assign-replacement`
  );
  return data;
}

// ---- Required-documents + required-information checklist ----

export interface ChecklistItem {
  label: string;
  description: string;
  status: 'provided' | 'missing' | 'present';
  matched: string | null;
  priority: number;
}

export interface CaseChecklist {
  required_documents: ChecklistItem[];
  required_information: ChecklistItem[];
}

export async function getCaseChecklist(caseId: string): Promise<CaseChecklist> {
  const { data } = await apiClient.get<CaseChecklist>(`/cases/${caseId}/checklist`);
  return data;
}
